import { useCallback, useRef, useState } from "react";
import { IShipmentEditFields, IShipmentEditState, IUseShipmentEditProps } from "./Shipmentedittypes ";
import { assignTrackingNumber, updateShipmentDetails } from "./Shipmenteditservice";
import toast from "react-hot-toast";

export const useShipmentEdit = ({
    mawb,
    type,
    uniqueValue,
    initialOrigin = '',
    initialDestination = '',
    initialPkts = 0,
    initialWeight = 0,
    isTrackingSet = false,
    onSaved,
}: IUseShipmentEditProps) => {
    const [state, setState] = useState<IShipmentEditState>({
        isEditing: false,
        isSaving: false,
        isTrackingNumberLocked: isTrackingSet,
        showTrackingWarning: false,
        fields: {
            origin: initialOrigin,
            destination: initialDestination,
            pkts: initialPkts,
            weight: initialWeight,
        },
        newTrackingNumber: '',
        errors: {},
    });

    // ── Use a ref to always read the latest state inside async callbacks ──────
    // This avoids stale closure bugs where saveChanges captures an old snapshot
    // of state (e.g. showTrackingWarning is false even after requestTrackingUpdate
    // set it to true).
    const stateRef = useRef(state);
    stateRef.current = state;

    // ── Helpers ───────────────────────────────────────────────────────────────

    const validate = (
        fields: IShipmentEditFields,
        newTracking: string,
        isTrackingLocked: boolean
    ): Partial<Record<keyof IShipmentEditFields | 'trackingNumber', string>> => {
        const errs: Partial<Record<keyof IShipmentEditFields | 'trackingNumber', string>> = {};

        // if (!fields.origin.trim()) errs.origin = 'Required';
        // if (!fields.destination.trim()) errs.destination = 'Required';
        if (fields.origin.trim() && !fields.destination.trim()) {
            errs.destination = 'Required';
        }
        if (fields.destination.trim() && !fields.origin.trim()) {
            errs.origin = 'Required';
        }
        if (fields.pkts === '' || Number(fields.pkts) < 0) errs.pkts = 'Invalid';
        if (fields.weight === '' || Number(fields.weight) < 0) errs.weight = 'Invalid';

        if (!isTrackingLocked && newTracking.trim()) {
            if (type === 'air' && !/^\d{3}-\d{7,8}$/.test(newTracking.trim())) {
                errs.trackingNumber = 'Format: 157-12381937';
            }
        }

        return errs;
    };

    // ── Actions ───────────────────────────────────────────────────────────────

    const startEditing = useCallback(() => {
        setState((prev) => ({
            ...prev,
            isEditing: true,
            newTrackingNumber: '',
            errors: {},
        }));
    }, []);

    const cancelEditing = useCallback(() => {
        setState((prev) => ({
            ...prev,
            isEditing: false,
            showTrackingWarning: false,
            newTrackingNumber: '',
            errors: {},
            fields: {
                origin: initialOrigin,
                destination: initialDestination,
                pkts: initialPkts,
                weight: initialWeight,
            },
        }));
    }, [initialOrigin, initialDestination, initialPkts, initialWeight]);

    const updateField = useCallback(
        <K extends keyof IShipmentEditFields>(key: K, value: IShipmentEditFields[K]) => {
            setState((prev) => ({
                ...prev,
                fields: { ...prev.fields, [key]: value },
                errors: { ...prev.errors, [key]: undefined },
            }));
        },
        []
    );

    const updateNewTrackingNumber = useCallback((value: string) => {
        setState((prev) => ({
            ...prev,
            newTrackingNumber: value,
            errors: { ...prev.errors, trackingNumber: undefined },
        }));
    }, []);

    const requestTrackingUpdate = useCallback(() => {
        setState((prev) => ({ ...prev, showTrackingWarning: true }));
    }, []);

    const dismissTrackingWarning = useCallback(() => {
        setState((prev) => ({
            ...prev,
            showTrackingWarning: false,
            // newTrackingNumber: '',
        }));
    }, []);

    // ── saveChanges reads from stateRef.current (always fresh) ───────────────
    const saveChanges = useCallback(async () => {
        // Read latest state via ref — never stale, even after requestTrackingUpdate
        const { fields, newTrackingNumber, isTrackingNumberLocked, showTrackingWarning } =
            stateRef.current;

        const errors = validate(fields, newTrackingNumber, isTrackingNumberLocked);
        if (Object.keys(errors).length > 0) {
            setState((prev) => ({ ...prev, errors }));
            return;
        }

        // Show warning banner before one-time tracking number assignment.
        // On second call (from confirmTrackingUpdate), showTrackingWarning is
        // already true in stateRef so we skip this and proceed to save.
        if (!isTrackingNumberLocked && newTrackingNumber.trim() && !showTrackingWarning) {
            requestTrackingUpdate();
            return;
        }

        setState((prev) => ({ ...prev, isSaving: true }));

        try {
            // 1. Update shipment details (origin / destination / pkts / weight)
            await updateShipmentDetails({
                type,
                trackingNumber: mawb,
                origin: fields.origin,
                destination: fields.destination,
                pkts: Number(fields.pkts),
                weight: Number(fields.weight),
            });

            // 2. Assign tracking number (one-time, only if provided and not yet locked)
            const assignedTracking = newTrackingNumber.trim();
            if (!isTrackingNumberLocked && assignedTracking) {
                await assignTrackingNumber({
                    type,
                    uniqueValue,
                    trackingNumber: assignedTracking,
                });
                setState((prev) => ({ ...prev, isTrackingNumberLocked: true }));
            }

            toast.success('Shipment details updated successfully!');

            setState((prev) => ({
                ...prev,
                isEditing: false,
                isSaving: false,
                showTrackingWarning: false,
                newTrackingNumber: '',
                errors: {},
            }));

            onSaved?.({
                origin: fields.origin,
                destination: fields.destination,
                pkts: Number(fields.pkts),
                weight: Number(fields.weight),
                trackingNumber: assignedTracking || undefined,
            });

        }  catch (err: any) {
            const apiErrors = err.response?.data?.error;

            // Handle field-level validation errors
            if (Array.isArray(apiErrors)) {
                const formattedErrors: Record<string, string> = {};

                apiErrors.forEach((item: any) => {
                    if (item.field) {
                        formattedErrors[item.field] = item.message;
                    }
                });

                setState((prev) => ({
                    ...prev,
                    errors: formattedErrors,
                    isSaving: false,
                    showTrackingWarning: false,
                }));

                toast.error("Please fix the highlighted fields.");
                return;
            }

            // Generic error fallback
            toast.error(
                err.response?.data?.message ||
                err.response?.data?.error ||
                'Failed to save changes.'
            );

            setState((prev) => ({
                ...prev,
                isSaving: false,
                showTrackingWarning: false,
            }));
        }
    // stateRef is a ref — intentionally excluded from deps, it's always current
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [mawb, type, uniqueValue, onSaved, requestTrackingUpdate]);

const confirmTrackingUpdate = useCallback(() => {
    saveChanges();
}, [saveChanges]);

return {
    state,
    startEditing,
    cancelEditing,
    updateField,
    updateNewTrackingNumber,
    dismissTrackingWarning,
    confirmTrackingUpdate,
    saveChanges,
};
};